import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { io, type Socket } from "socket.io-client";
import { API_BASE } from "../utils/api";
import { BEARER_TOKEN_KEY } from "../utils/auth-client";
import { useAuth } from "./AuthContext";

/** Origin des API-Backends (Socket.IO läuft auf demselben Host wie /api). */
const socketOrigin = /^https?:\/\//i.test(API_BASE)
  ? new URL(API_BASE).origin
  : typeof window !== "undefined"
    ? window.location.origin
    : "http://localhost";

type Ack<T> = { ok: true } & T;
type AckErr = { error: { code: string } };

type ChatSocketContextType = {
  connected: boolean;
  /** Event-Listener registrieren; gibt eine Cleanup-Funktion zurück. */
  on: (event: string, handler: (...args: any[]) => void) => () => void;
  /** Feuern ohne Bestätigung. */
  emit: (event: string, payload?: unknown) => void;
  /** Feuern mit Ack (Promise); wirft bei Timeout/Error-Ack. */
  emitAck: <T = unknown>(event: string, payload?: unknown, timeoutMs?: number) => Promise<Ack<T>>;
};

const ChatSocketContext = createContext<ChatSocketContextType | undefined>(undefined);

/**
 * Eine App-weite Socket.IO-Verbindung für den internen Chat (Namespace `/chat`,
 * Transport-Path `/api/chat/socket.io`). Auth über den better-auth-Bearer aus dem
 * localStorage (derselbe Key wie der globale fetch-Patch); die `auth`-Callback-
 * Form sorgt dafür, dass Reconnects einen aufgefrischten Token verwenden.
 * Die bestehende SSE-Glocke bleibt davon unberührt.
 */
export const ChatSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isPendingApproval } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || isPendingApproval) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      setConnected(false);
      return;
    }

    const socket = io(`${socketOrigin}/chat`, {
      path: "/api/chat/socket.io",
      transports: ["websocket"],
      withCredentials: true,
      autoConnect: true,
      auth: (cb) => cb({ token: localStorage.getItem(BEARER_TOKEN_KEY) || "" }),
    });
    socketRef.current = socket;

    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);

    // App-Heartbeat (~25s) für Presence (online-Erkennung).
    const ping = setInterval(() => {
      if (socket.connected) socket.emit("presence:ping");
    }, 25_000);

    return () => {
      clearInterval(ping);
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [isAuthenticated, isPendingApproval]);

  const value = useMemo<ChatSocketContextType>(
    () => ({
      connected,
      on: (event, handler) => {
        const s = socketRef.current;
        if (!s) return () => undefined;
        s.on(event, handler);
        return () => s.off(event, handler);
      },
      emit: (event, payload) => {
        socketRef.current?.emit(event, payload);
      },
      emitAck: <T,>(event: string, payload?: unknown, timeoutMs = 10_000) =>
        new Promise<Ack<T>>((resolve, reject) => {
          const s = socketRef.current;
          if (!s || !s.connected) {
            reject(new Error("socket_disconnected"));
            return;
          }
          let settled = false;
          const timer = setTimeout(() => {
            if (!settled) {
              settled = true;
              reject(new Error("ack_timeout"));
            }
          }, timeoutMs);
          s.emit(event, payload, (res: Ack<T> | AckErr) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            if (res && (res as AckErr).error) {
              reject(new Error((res as AckErr).error.code));
            } else {
              resolve(res as Ack<T>);
            }
          });
        }),
    }),
    [connected],
  );

  return <ChatSocketContext.Provider value={value}>{children}</ChatSocketContext.Provider>;
};

export const useChatSocket = (): ChatSocketContextType => {
  const ctx = useContext(ChatSocketContext);
  if (!ctx) throw new Error("useChatSocket must be used within a ChatSocketProvider");
  return ctx;
};

/**
 * Wie {@link useChatSocket}, wirft aber NICHT ohne Provider (gibt dann
 * `undefined`). Für optionale Live-Andockpunkte (z. B. `useVorgangNotesLive`),
 * die ohne Socket-Provider einfach still ruhen sollen.
 */
export const useChatSocketOptional = (): ChatSocketContextType | undefined =>
  useContext(ChatSocketContext);
